import * as df from 'date-fns';
import * as React from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';

import scrollToTop from '../../../hoc/scrollToTop';

import BooksSubmitButton from '../../../../components/books/common/buttons/BooksSubmitButton';
import BooksTextButton from '../../../../components/books/common/buttons/BooksTextButton';
import BooksTimeItem from '../../../../components/books/common/texts/BooksTimeItem';
import BooksLabeledContainer from '../../../../components/books/model1/BooksLabeledContainer';
import BooksSelectedMenu from '../../../../components/books/model1/BooksSelectedMenu';
import InputSchoolCalendarDateTimePicker from './InputSchoolCalendarDateTimePicker';

import * as IBooks from '../../../../interfaces/merchants/resources/books';
import * as IInputs from '../../../../interfaces/merchants/resources/books/inputs';
import * as Actions from '../../../../redux/actions/merchants/resources/books';
import { DateTimePickerQuery } from '../../../../redux/actions/merchants/resources/books/api';
import * as Inputs from '../../../../redux/actions/merchants/resources/books/inputs';
import { Dispatch } from '../../../../redux/reducers/merchants/resources/books/inputs';

interface ElementProps {
  modelType: IBooks.ModelType;
  settings: IBooks.SettingBookingDateTimePicker;
  postable: boolean;
  backToModifyPage: () => void;
  onSubmit: () => void;
  onRestart: () => void;
}

interface StateProps {
  inputs: IInputs.State;
}

interface DispatchProps {
  actions: {
    clearDateTimePicker: () => void;
    setDateTimePicker: (q: DateTimePickerQuery) => Promise<void>;
    handleSubmit: (
      reservationId: number,
      schoolTimeSlots: IInputs.SchoolTimeSlot[],
    ) => Promise<void>;
    handleClearTargets: (course: boolean) => void;
  };
}

type Props = ElementProps & StateProps & DispatchProps & InjectedIntlProps;

interface State {
  baseDate: Date;
  selecting: boolean;
  selectedSchoolTimeSlots?: IBooks.SchoolTimeSlot[];
}

class InputSchoolCalendar extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const {
      settings: { selectedTimeSlot, selectedLessonId },
      inputs: { lesson, schoolTimeSlots },
    } = this.props;
    const selectedSchoolTimeSlots = schoolTimeSlots
      ? schoolTimeSlots
      : selectedTimeSlot && lesson!.id === selectedLessonId
      ? [selectedTimeSlot]
      : undefined;
    this.state = {
      baseDate: new Date(),
      selecting: false,
      selectedSchoolTimeSlots,
    };
    this.handleChangeTarget = this.handleChangeTarget.bind(this);
    this.handleStartSelectingDate = this.handleStartSelectingDate.bind(this);

    this.handleChangeBaseDate = this.handleChangeBaseDate.bind(this);
    this.handleChangeTimeSlots = this.handleChangeTimeSlots.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  public async componentDidMount() {
    const { selectedSchoolTimeSlots } = this.state;
    this.props.actions.clearDateTimePicker();
    const today = selectedSchoolTimeSlots
      ? df.parse(selectedSchoolTimeSlots[0].date)
      : new Date();
    this.handleChangeBaseDate(today);
  }

  private handleChangeBaseDate(baseDate: Date) {
    const { actions, inputs } = this.props;
    const { merchantId, resourceId, lesson } = inputs;

    if (!resourceId || !lesson || !merchantId) {
      return;
    }

    this.setState({ ...this.state, baseDate });

    actions.setDateTimePicker({
      merchantId,
      resourceId,
      lesson,
      start: df.startOfMonth(baseDate),
      end: df.endOfMonth(baseDate),
    });
  }

  private handleStartSelectingDate() {
    this.setState({
      ...this.state,
      selecting: true,
    });
  }

  private handleChangeTarget(lesson: boolean, _option: boolean) {
    this.props.actions.handleClearTargets(lesson);
    this.props.backToModifyPage();
  }

  private handleChangeTimeSlots(timeSlots: IBooks.SchoolTimeSlot[]) {
    const timeSlot = timeSlots[0];
    this.setState({
      ...this.state,
      selecting: false,
      baseDate: df.parse(timeSlot.date),
      selectedSchoolTimeSlots: timeSlots,
    });
  }

  private async handleSubmit() {
    const { inputs, actions, onSubmit, onRestart } = this.props;
    const { selectedSchoolTimeSlots } = this.state;
    try {
      await actions.handleSubmit(
        inputs.reservationId!,
        selectedSchoolTimeSlots!,
      );
      onSubmit();
    } catch (e) {
      if (e instanceof Inputs.RestartReservationError) {
        onRestart();
      }
    }
  }

  public render() {
    const {
      inputs: { lesson },
      settings: { showVacancy, timeSlots },
      postable,
      intl,
    } = this.props;
    const { baseDate, selecting, selectedSchoolTimeSlots } = this.state;

    if (!lesson) {
      return null;
    }

    return (
      <div>
        {!selecting ? (
          <div>
            <BooksLabeledContainer
              label={intl.formatMessage({
                id: 'books.formsLabel.selectedBooks',
              })}
              contentsPadding={true}
              paddingTop={true}
            >
              <BooksSelectedMenu
                name={lesson.name}
                options={[]}
                changeTarget={() => this.handleChangeTarget(true, false)}
              />
            </BooksLabeledContainer>
            <BooksLabeledContainer
              label={intl.formatMessage({
                id: 'books.booksMenu.bookDate',
              })}
              paddingTop={true}
            >
              {selectedSchoolTimeSlots ? (
                <BooksTimeItem
                  date={selectedSchoolTimeSlots[0].date}
                  startTime={selectedSchoolTimeSlots[0].startTime}
                  endTime={selectedSchoolTimeSlots[0].endTime}
                  instructors={selectedSchoolTimeSlots[0].instructors.map(
                    i => i.name,
                  )}
                  withPadding={true}
                  onClick={this.handleStartSelectingDate}
                />
              ) : (
                <BooksTextButton onClick={this.handleStartSelectingDate}>
                  {intl.formatMessage({
                    id: 'books.forms.addDateAndTime',
                  })}
                </BooksTextButton>
              )}
            </BooksLabeledContainer>
            <BooksSubmitButton
              disabled={!selectedSchoolTimeSlots || !postable}
              postable={postable}
              onClick={this.handleSubmit}
            >
              {intl.formatMessage({ id: 'books.submitLabel.next' })}
            </BooksSubmitButton>
          </div>
        ) : (
          <InputSchoolCalendarDateTimePicker
            postable={postable}
            selection={'single'}
            baseDate={baseDate}
            timeSlots={timeSlots}
            showVacancy={showVacancy}
            onChange={this.handleChangeTimeSlots}
            onChangeBaseDate={this.handleChangeBaseDate}
            selectedTimeSlots={
              selectedSchoolTimeSlots ? selectedSchoolTimeSlots : []
            }
          />
        )}
      </div>
    );
  }
}

const mapStateProps = ({
  merchantsResourcesBooksInputs,
}: {
  merchantsResourcesBooksInputs: IInputs.State;
}): StateProps => ({
  inputs: merchantsResourcesBooksInputs,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  actions: {
    handleClearTargets: (course: boolean, options: boolean, staff?: boolean) =>
      Inputs.clearBookingTargets({ course, options, staff })(dispatch),
    handleSubmit: (
      reservationId: number,
      schoolTimeSlots: IInputs.SchoolTimeSlot[],
    ) =>
      Inputs.inputBookingSchoolTimeSlot(
        reservationId,
        schoolTimeSlots,
      )(dispatch),
    clearDateTimePicker: () => Actions.clearDateTimePicker()(dispatch),
    setDateTimePicker: (q: DateTimePickerQuery) =>
      Actions.setDateTimePicker(q)(dispatch),
  },
});

export default connect(
  mapStateProps,
  mapDispatchToProps,
)(injectIntl(scrollToTop(InputSchoolCalendar)));
